/* eslint-disable i18next/no-literal-string */
import { useTranslations } from '@aikoa/i18n/react'
import { View } from 'react-native'
import { IntervalInput, type IntervalValue } from '~/components/admin/IntervalInput'
import { RadioOption } from '~/components/admin/RadioOption'
import { Label } from '~/components/typography/Content'
import { Checkbox } from '~/components/ui/checkbox'
import { Input } from '~/components/ui/input'
import { Text } from '~/components/ui/text'

export type PromotionType = 'free_months' | 'percentage' | 'amount'

const PROMOTION_TYPES: PromotionType[] = ['percentage', 'amount', 'free_months']

export type ReferralGenerationFormValues = {
  enabled: boolean
  codeCount: string
  freeMonths: string
  cascading: boolean
}

export type PromotionFormValues = {
  promotionCode: string
  titleKey: string
  promotionAmount: string
  promotionType: PromotionType
  validDuration: IntervalValue
  maxActivations: string
  referralGeneration: ReferralGenerationFormValues
}

type PromotionFormProps = {
  values: PromotionFormValues
  onChange: (values: PromotionFormValues) => void
}

export function PromotionForm({ values, onChange }: PromotionFormProps) {
  const { t } = useTranslations('admin')

  const updateField = <K extends keyof PromotionFormValues>(
    field: K,
    value: PromotionFormValues[K]
  ) => {
    onChange({ ...values, [field]: value })
  }

  return (
    <View className="gap-4">
      <View className="gap-2">
        <Label>{t(($) => $.promotions.fields.code)}</Label>
        <Input
          value={values.promotionCode}
          onChangeText={(text) => updateField('promotionCode', text.toUpperCase())}
          placeholder={t(($) => $.promotions.fields.codePlaceholder)}
          autoCapitalize="characters"
          variant="box"
          className="bg-white"
        />
      </View>

      <View className="gap-2">
        <Label>{t(($) => $.promotions.fields.titleKey)}</Label>
        <Input
          value={values.titleKey}
          onChangeText={(text) => updateField('titleKey', text)}
          placeholder={t(($) => $.promotions.fields.titleKeyPlaceholder)}
          variant="box"
          className="bg-white"
        />
      </View>

      <View className="gap-2">
        <Label>{t(($) => $.promotions.fields.type)}</Label>
        <View className="gap-2">
          {PROMOTION_TYPES.map((type) => (
            <RadioOption
              key={type}
              value={type}
              selected={values.promotionType === type}
              onSelect={(selectedType) => updateField('promotionType', selectedType)}
              label={t(($) => $.promotions.types[type])}
            />
          ))}
        </View>
      </View>

      <View className="gap-2">
        <Label>{t(($) => $.promotions.fields.amount)}</Label>
        <Input
          value={values.promotionAmount}
          onChangeText={(text) => updateField('promotionAmount', text)}
          placeholder={
            values.promotionType === 'percentage'
              ? '10'
              : values.promotionType === 'free_months'
                ? '3'
                : '50.00'
          }
          keyboardType="decimal-pad"
          variant="box"
          className="bg-white"
        />
        <Text className="text-xs text-neutral-500">
          {values.promotionType === 'percentage' &&
            t(($) => $.promotions.fields.amountHintPercentage)}
          {values.promotionType === 'amount' && t(($) => $.promotions.fields.amountHintAmount)}
          {values.promotionType === 'free_months' &&
            t(($) => $.promotions.fields.amountHintFreeMonths)}
        </Text>
      </View>

      {values.promotionType !== 'free_months' && (
        <View className="gap-2">
          <Label>{t(($) => $.promotions.fields.validDuration)}</Label>
          <IntervalInput
            value={values.validDuration}
            onChange={(interval) => updateField('validDuration', interval)}
          />
          <Text className="text-xs text-neutral-500">
            {t(($) => $.promotions.fields.validDurationHint)}
          </Text>
        </View>
      )}

      <View className="gap-2">
        <Label>{t(($) => $.promotions.fields.maxActivations)}</Label>
        <Input
          value={values.maxActivations}
          onChangeText={(text) => updateField('maxActivations', text)}
          placeholder={t(($) => $.promotions.fields.maxActivationsPlaceholder)}
          keyboardType="number-pad"
          variant="box"
          className="bg-white"
        />
        <Text className="text-xs text-neutral-500">
          {t(($) => $.promotions.fields.maxActivationsHint)}
        </Text>
      </View>

      <View className="gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
        <View className="flex-row items-center gap-3">
          <Checkbox
            checked={values.referralGeneration.enabled}
            onCheckedChange={(checked) =>
              updateField('referralGeneration', {
                ...values.referralGeneration,
                enabled: !!checked,
              })
            }
          />
          <Label>{t(($) => $.promotions.fields.referralGenerationLabel)}</Label>
        </View>

        <Text className="text-xs text-neutral-500">
          {t(($) => $.promotions.fields.referralGenerationHint)}
        </Text>

        {values.referralGeneration.enabled && (
          <View className="gap-3">
            <View className="gap-2">
              <Label>{t(($) => $.promotions.fields.referralCodeCount)}</Label>
              <Input
                value={values.referralGeneration.codeCount}
                onChangeText={(text) =>
                  updateField('referralGeneration', {
                    ...values.referralGeneration,
                    codeCount: text,
                  })
                }
                placeholder="5"
                keyboardType="number-pad"
                variant="box"
                className="bg-white"
              />
            </View>

            <View className="gap-2">
              <Label>{t(($) => $.promotions.fields.referralFreeMonths)}</Label>
              <Input
                value={values.referralGeneration.freeMonths}
                onChangeText={(text) =>
                  updateField('referralGeneration', {
                    ...values.referralGeneration,
                    freeMonths: text,
                  })
                }
                placeholder="3"
                keyboardType="number-pad"
                variant="box"
                className="bg-white"
              />
            </View>

            <View className="flex-row items-center gap-3">
              <Checkbox
                checked={values.referralGeneration.cascading}
                onCheckedChange={(checked) =>
                  updateField('referralGeneration', {
                    ...values.referralGeneration,
                    cascading: !!checked,
                  })
                }
              />
              <View className="flex-1 gap-0.5">
                <Label>{t(($) => $.promotions.fields.referralCascadingLabel)}</Label>
                <Text className="text-xs text-neutral-500">
                  {t(($) => $.promotions.fields.referralCascadingHint)}
                </Text>
              </View>
            </View>
          </View>
        )}
      </View>
    </View>
  )
}

export function isPromotionFormValid(values: PromotionFormValues): boolean {
  const baseValid =
    values.promotionCode.trim().length > 0 &&
    values.titleKey.trim().length > 0 &&
    Number.parseFloat(values.promotionAmount) > 0

  if (!baseValid) {
    return false
  }

  if (values.referralGeneration.enabled) {
    const codeCount = Number.parseInt(values.referralGeneration.codeCount, 10)
    const freeMonths = Number.parseInt(values.referralGeneration.freeMonths, 10)

    if (!codeCount || codeCount < 1 || !freeMonths || freeMonths < 1) {
      return false
    }
  }

  return true
}

export function buildPromotionDataFromForm(
  referralGeneration: ReferralGenerationFormValues
): { referralGeneration: { codeCount: number; freeMonths: number; cascading: boolean } } | null {
  if (!referralGeneration.enabled) {
    return null
  }

  return {
    referralGeneration: {
      codeCount: Number.parseInt(referralGeneration.codeCount, 10),
      freeMonths: Number.parseInt(referralGeneration.freeMonths, 10),
      cascading: referralGeneration.cascading,
    },
  }
}

export function parsePromotionDataToForm(
  promotionData: Record<string, unknown> | null
): ReferralGenerationFormValues {
  if (!promotionData?.referralGeneration) {
    return getDefaultReferralGenerationFormValues()
  }

  const config = promotionData.referralGeneration as {
    codeCount: number
    freeMonths: number
    cascading?: boolean
  }

  return {
    enabled: true,
    codeCount: String(config.codeCount),
    freeMonths: String(config.freeMonths),
    cascading: config.cascading !== false,
  }
}

export function getDefaultReferralGenerationFormValues(): ReferralGenerationFormValues {
  return {
    enabled: false,
    codeCount: '',
    freeMonths: '',
    cascading: true,
  }
}

export function getDefaultPromotionFormValues(): PromotionFormValues {
  return {
    promotionCode: '',
    titleKey: '',
    promotionAmount: '',
    promotionType: 'percentage',
    validDuration: { value: '', unit: 'months' },
    maxActivations: '',
    referralGeneration: getDefaultReferralGenerationFormValues(),
  }
}
